import type { ComponentProps, PropsWithChildren } from 'react';
import React from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import type {
  AlignItemsProps,
  BackgroundColorProps,
  BorderColorProps,
  BorderProps,
  BorderRadiusProps,
  BorderStyleProps,
  BorderWidthProps,
  DisplayProps,
  JustifyContentProps,
  MaxHeightProps,
  MaxWidthProps,
  SizeProps,
  TextAlignProps,
  WidthProps,
} from 'styled-system';
import {
  alignItems,
  backgroundColor,
  border,
  borderColor,
  borderRadius,
  borderStyle,
  borderWidth,
  display,
  justifyContent,
  maxHeight,
  maxWidth,
  size,
  space,
  textAlign,
  themeGet,
  width,
} from 'styled-system';

import type { FillProps, SpaceProps } from 'common/theme/system-types';
import { focusOutlineCSSWithOffsetAndRadius } from '../../theme/styles';
import type { CursorProps } from '../../theme/system-utilities';
import { cursor, fill } from '../../theme/system-utilities';
import Icon from '../Icon';
import { HeadlineH7 } from '../Typography';
import type { IconStylesProps, SectionBtnStylesProps } from './types';

interface SectionButtonProps extends PropsWithChildren {
  sectionTitleId: string;
  sectionRegionId: string;
  isExpanded: boolean;
  withHover?: boolean;
  hasBottomBorder?: boolean;
  withTopBorderRadius?: boolean;
  withBottomBorderRadius?: boolean;
  iconStyles?: IconStylesProps;
  sectionBtnStyles?: SectionBtnStylesProps;
  keepBorderWhenExpanded?: boolean;
  ref?: React.Ref<HTMLButtonElement>;
}

const ICON_SIZE = 16;

const Button = styled.button<
  AlignItemsProps &
    BackgroundColorProps &
    BorderProps &
    BorderRadiusProps &
    BorderWidthProps &
    CursorProps &
    DisplayProps &
    JustifyContentProps &
    SpaceProps &
    TextAlignProps &
    WidthProps & {
      keepBorderWhenExpanded?: boolean;
      hasBottomBorder?: boolean;
      withTopBorderRadius?: boolean;
      withBottomBorderRadius?: boolean;
    }
>`
  font: inherit;
  ${alignItems};
  ${backgroundColor};
  ${borderRadius};
  ${border};
  ${cursor};
  ${display};
  ${justifyContent};
  ${space};
  ${textAlign};
  ${width};

  ${focusOutlineCSSWithOffsetAndRadius};

  ${props =>
    props.withTopBorderRadius &&
    css`
      border-top-left-radius: ${themeGet('radii.medium')(props)};
      border-top-right-radius: ${themeGet('radii.medium')(props)};
    `};

  ${props =>
    props.withBottomBorderRadius &&
    css`
      border-bottom-left-radius: ${themeGet('radii.medium')(props)};
      border-bottom-right-radius: ${themeGet('radii.medium')(props)};
    `};
`;

const ButtonStyles: ComponentProps<typeof Button> = {
  alignItems: 'center',
  backgroundColor: 'sys.neutral.background.default',
  border: 'none',
  borderRadius: 0,
  cursor: 'pointer',
  display: 'flex',
  justifyContent: 'space-between',
  m: 'sp_0',
  p: 'sp_0',
  textAlign: 'left',
  width: 1,
};

const ButtonWithHover = styled(Button)<BorderColorProps & BorderStyleProps & MaxWidthProps>`
  ${borderColor};
  ${borderStyle};
  ${borderWidth};
  ${maxWidth};

  ${props =>
    !props.keepBorderWhenExpanded &&
    props.hasBottomBorder &&
    css`
      border-bottom-color: ${themeGet('colors.sys.neutral.border.weak')(props)};
    `};

  ${props =>
    props.keepBorderWhenExpanded &&
    css`
      border-top-color: ${themeGet('colors.sys.neutral.border.weak')(props)};
    `};

  &:hover {
    border-radius: ${themeGet('radii.medium')};
    border-color: ${themeGet('colors.sys.neutral.border.hover')};
    position: relative;
  }

  &:focus-visible {
    outline: 1px solid ${themeGet('colors.sys.secondary.border.default')};
    border: 1px solid ${themeGet('colors.sys.secondary.border.default')};
    border-radius: ${themeGet('radii.medium')};
    outline-offset: -${themeGet('space.sp_2')}px;
    position: relative;
  }
`;

const ButtonWithHoverStyles: ComponentProps<typeof ButtonWithHover> = {
  ...ButtonStyles,
  borderColor: 'transparent',
  borderWidth: '1px',
  border: '1px',
  borderStyle: 'solid',
  px: ['sp_16', 'sp_24'],
  py: ['sp_20', null, 'sp_24'],
};

const UnstyledArrow = styled(Icon)<
  SizeProps & MaxHeightProps & MaxWidthProps & FillProps & { down: boolean }
>`
  transform: rotate(${props => (props.down ? 0 : 180)}deg);
  ${fill};
  ${size};
  ${maxHeight};
  ${maxWidth};
`;

const Arrow = (props: ComponentProps<typeof UnstyledArrow>) => (
  <UnstyledArrow
    size={ICON_SIZE}
    maxWidth={ICON_SIZE}
    maxHeight={ICON_SIZE}
    fill="sys.neutral.border.hover"
    role="presentation"
    {...props}
  />
);

const SectionButton = ({
  sectionTitleId,
  sectionRegionId,
  isExpanded,
  children,
  withHover,
  hasBottomBorder,
  withTopBorderRadius,
  withBottomBorderRadius,
  iconStyles,
  sectionBtnStyles,
  keepBorderWhenExpanded,
  ref,
}: SectionButtonProps) => {
  const SectionBtn = withHover ? ButtonWithHover : Button;
  const { name, ...restIconStyles } = iconStyles || {};

  return (
    <SectionBtn
      ref={ref}
      aria-expanded={isExpanded}
      aria-controls={sectionRegionId}
      id={sectionTitleId}
      data-testid="accordion-button"
      hasBottomBorder={!isExpanded && hasBottomBorder}
      keepBorderWhenExpanded={keepBorderWhenExpanded}
      withTopBorderRadius={withTopBorderRadius}
      withBottomBorderRadius={withBottomBorderRadius}
      {...(withHover ? ButtonWithHoverStyles : ButtonStyles)}
      {...(sectionBtnStyles ? sectionBtnStyles : {})}
    >
      <HeadlineH7 as="span">{children}</HeadlineH7>
      <Arrow
        down={!isExpanded}
        name={name || 'arrowDownStrong'}
        {...(iconStyles ? restIconStyles : {})}
      />
    </SectionBtn>
  );
};

SectionButton.displayName = 'SectionButton';

export default SectionButton;
